import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import api from '../../../../api/client';
import Logo from '../../../../assets/AptiviewLogo.png';
import ReCaptcha from '../../../common/ReCaptcha';
import AIInterviewTheme from './AIInterviewTheme';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'react-toastify/dist/ReactToastify.css';
import DeviceWarning from '../../../../utils/DeviceWarning';

export default function AdminLogin({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const token = localStorage.getItem('token');
  // Support redirectTo from state, query params, or sessionStorage (survives page reload)
  const queryRedirectTo = new URLSearchParams(location.search).get('redirectTo');
  const redirectTo = location.state?.redirectTo || queryRedirectTo || sessionStorage.getItem('postLoginRedirectTo');
  const currentYear = new Date().getFullYear();

  // --- Two-step login state ---
  const [loginStep, setLoginStep] = useState('email'); // 'email' | 'password'
  const [verifying, setVerifying] = useState(false);
  const [authMethod, setAuthMethod] = useState(null); // future: 'password' | 'sso_redirect' | 'otp'
  const [emailError, setEmailError] = useState('');
  const [slideDirection, setSlideDirection] = useState(''); // 'left' | 'right' | ''

  // Persist redirectTo to sessionStorage so it survives window.location.reload() during login
  useEffect(() => {
    if (redirectTo && !token) {
      sessionStorage.setItem('postLoginRedirectTo', redirectTo);
    }
  }, [redirectTo, token]);

  // Check for message parameter in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const message = params.get('message');
    if (message) {
      toast.info(message, {
        icon: "ℹ️"
      });
      // Clear the message from URL without reloading the page
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, navigate, location.pathname]);

  useEffect(() => {
    if (token) {
      if (redirectTo) {
        // Clear the stored redirect after using it
        sessionStorage.removeItem('postLoginRedirectTo');
        navigate(redirectTo, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [token, navigate, redirectTo]);

  // Helper function to handle navigation and success events after login
  const handlePostLoginNavigation = () => {
    // Dispatch custom event to notify about successful login (for expiry warning)
    window.dispatchEvent(new CustomEvent('userLoggedIn', {
      detail: { timestamp: new Date().toISOString() }
    }));

    const target = redirectTo || '/dashboard';
    if (redirectTo) {
      sessionStorage.removeItem('postLoginRedirectTo');
    }
    navigate(target, { replace: true });
  };

  // Helper function to decode token, save user data, role, language, and reload if necessary
  const handleLoginSuccessData = (token) => {
    localStorage.setItem('token', token);
    let decoded = null;
    try {
      const payload = token.split('.')[1];
      decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));

      localStorage.setItem(
        'user',
        JSON.stringify({
          id: decoded.user_id || decoded.id,
          email: decoded.email,
          firstName: decoded.first_name,
          lastName: decoded.last_name,
          isStaff: decoded.is_staff || false,
          is_company_admin: decoded.is_company_admin || false,
          is_superuser: decoded.is_superuser || false,
          is_usermanager: decoded.is_usermanager || false,
          company: decoded.company_id || decoded.company,
          companyName: decoded.company_name || '',
          admin_companies: decoded.admin_companies || [],
          user_profile_picture: decoded.user_profile_picture
            ? (decoded.user_profile_picture.startsWith('http')
              ? decoded.user_profile_picture
              : process.env.REACT_APP_FRONTEND_URL + (decoded.user_profile_picture.startsWith('/') ? '' : '/') + decoded.user_profile_picture)
            : '',
          // Add expiry date if available in token
          expiryDate: decoded.expiry_date || decoded.exp ? new Date(decoded.exp * 1000).toISOString() : null,
          default_language: decoded.default_language || 'en',
          available_languages: decoded.available_languages || ['en', 'hi', 'ar', 'fr']
        })
      );
      let role = 'Candidate';
      if (decoded?.is_superuser === true) {
        role = 'Super Admin';
      } else if (Array.isArray(decoded?.admin_companies) && decoded.admin_companies.length > 0) {
        role = 'Company Admin';
      }
      localStorage.setItem('role', role);

      // Set default language from token
      const defaultLang = decoded.default_language || 'en';
      localStorage.setItem('i18nextLng', defaultLang);
      if (!redirectTo) {
        window.location.reload(); // Reload to apply language
      }

    } catch (decodeError) {
      console.error('Error decoding token:', decodeError);
    }
    return decoded;
  };

  // --- Step 1: Verify email exists ---
  const handleEmailNext = async (e) => {
    e.preventDefault();
    setEmailError('');

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) return;

    // Basic client-side email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setEmailError('Please enter a valid email address.');
      return;
    }

    if (!recaptchaToken) {
      toast.error(t('auth.recaptchaRequired'), {
        icon: "⚠️"
      });
      return;
    }

    setVerifying(true);
    try {
      const res = await api.post('auth/verify-identifier/', { email: trimmedEmail });
      if (res.data.exists) {
        setAuthMethod(res.data.auth_method || 'password');
        setSlideDirection('left');
        setTimeout(() => setLoginStep('password'), 30);
      } else {
        setEmailError('No account found with this email. Please check for typos or create a new account.');
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setEmailError('Too many attempts. Please wait a moment and try again.');
      } else {
        const errorMsg = err.response?.data?.error ||
          err.response?.data?.detail ||
          err.response?.data?.message ||
          'Something went wrong. Please try again.';
        setEmailError(errorMsg);
      }
    } finally {
      setVerifying(false);
    }
  };

  // --- Step 2 back to Step 1 ---
  const handleBackToEmail = () => {
    setSlideDirection('right');
    setTimeout(() => {
      setLoginStep('email');
      setPassword('');
      setRecaptchaToken('');
      setEmailError('');
    }, 30);
  };

  // --- Step 2: Login with password (existing logic, unchanged) ---
  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const res = await api.post('auth/login/', {
        email: email.toLowerCase(),
        password,
        recaptcha_response: recaptchaToken
      });
      if (res.data.token) {
        if (res.data.requires_consent) {
          localStorage.setItem('requires_consent', 'true');
        } else {
          localStorage.removeItem('requires_consent');
        }
        handleLoginSuccessData(res.data.token);

        toast.success(t('auth.loginSuccess'), {
          icon: "🎉"
        });

        onLogin(res.data);
        handlePostLoginNavigation();
      } else {
        throw new Error('No token received from server');
      }
    } catch (err) {
      let errorMessage = t('auth.loginFailed');

      if (err.response) {
        const status = err.response.status;
        const responseData = err.response.data;

        if (status === 401) {
          if (responseData?.error?.toLowerCase().includes('credential') ||
            responseData?.detail?.toLowerCase().includes('credential') ||
            responseData?.error?.toLowerCase().includes('invalid') ||
            responseData?.detail?.toLowerCase().includes('invalid')) {
            errorMessage = t('auth.invalidCredentials');
          } else {
            errorMessage = 'Authentication failed. Please check your credentials.';
          }
        } else if (status === 400) {
          errorMessage = responseData?.error || responseData?.detail || 'Invalid request. Please check your input.';
        } else if (status === 403) {
          errorMessage = 'Access forbidden. You may not have permission to access this account.';
        } else if (status === 429) {
          errorMessage = 'Too many login attempts. Please wait a few minutes before trying again.';
        } else if (status >= 500) {
          errorMessage = 'Server error. Please try again later or contact support.';
        } else {
          errorMessage = responseData?.error || responseData?.detail || errorMessage;
        }
      } else if (err.message) {
        if (err.message.includes('Network Error')) {
          errorMessage = 'Network error. Please check your internet connection.';
        } else {
          errorMessage = err.message;
        }
      }

      toast.error(errorMessage, {
        icon: "❌"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const response = await api.post('auth/google/', {
        credential: credentialResponse.credential,
      });

      if (response.data.token) {
        if (response.data.requires_consent) {
          localStorage.setItem('requires_consent', 'true');
        } else {
          localStorage.removeItem('requires_consent');
        }
        handleLoginSuccessData(response.data.token);

        toast.success('Google login successful! Redirecting...', {
          icon: "🎉"
        });

        onLogin(response.data);
        handlePostLoginNavigation();
      } else {
        throw new Error('No token received from server');
      }
    } catch (err) {
      console.error('Google login error:', err);
      toast.error(err.response?.data?.error || err.message || 'Google login failed. Please try again.', {
        icon: "❌"
      });
    }
  };

  const handleGoogleError = () => {
    toast.error('Google login failed. Please try again.', {
      icon: "❌"
    });
  };

  // --- Email initial for avatar ---
  const emailInitial = email ? email.charAt(0).toUpperCase() : '?';

  return (
    <div className="login-container">
      <DeviceWarning />
      <div className="login-bg-overlay" />
      <div className="orb orb-left" />
      <div className="orb orb-right" />

      <div className="row flex-fill w-100 m-0" style={{ height: '100%' }}>
        {/* Left Side - AI Interview Theme - Hidden on mobile */}
        <AIInterviewTheme
          title="AI-Powered Interviews"
          description="Experience the future of recruitment with our intelligent interview platform. Advanced AI assessments, real-time analytics, and comprehensive candidate evaluation."
        />

        {/* Right Side - Login Form - Full width on mobile */}
        <div className="col-lg-4 col-12 d-flex justify-content-center p-3 p-lg-4 login-column-wrapper">
          <div className="w-100 login-card-wrapper">
            {/* Subtle top gradient accent bar */}
            <div className="login-accent-bar" />

            {/* Logo and Header */}
            <div className="text-center" style={{ marginBottom: '1.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                <img src={Logo} alt="AptiView Logo" className="logo-img" />
              </div>
              <h1 className="login-welcome-title">{t('common.welcome')}</h1>
              <p className="login-welcome-subtitle">
                {loginStep === 'email' ? 'Sign in to your account' : 'Enter your password to continue'}
              </p>

              {/* Step indicator dots */}
              <div className="step-indicator">
                <div className={`indicator-dot ${loginStep === 'email' ? 'active-email' : 'inactive'}`} />
                <div className={`indicator-dot ${loginStep === 'password' ? 'active-password' : 'inactive'}`} />
              </div>
            </div>

            {/* ========== STEP 1: EMAIL ========== */}
            {loginStep === 'email' && (
              <div
                key="step-email"
                className={
                  slideDirection === 'right' || slideDirection === 'left'
                    ? 'step-slide-left'
                    : 'step-active'
                }
              >
                <form onSubmit={handleEmailNext} noValidate>
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label htmlFor="admin-email" className="login-label">Email address</label>
                    <div style={{ position: 'relative' }}>
                      <div className={`input-icon-wrapper ${emailError ? 'error' : ''}`}>
                        <i className="bi bi-envelope"></i>
                      </div>
                      <input
                        id="admin-email"
                        type="email"
                        placeholder={t('auth.emailPlaceholder')}
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                        required
                        disabled={verifying}
                        autoFocus
                        className={`login-input ${emailError ? 'error' : ''}`}
                      />
                    </div>
                    {/* Email error message */}
                    {emailError && (
                      <div className="error-msg-box">
                        <i className="bi bi-exclamation-circle" style={{ marginTop: '0.1rem', flexShrink: 0 }}></i>
                        <span>
                          {emailError}{' '}
                          {emailError.includes('No account') && (
                            <Link to="/signup" className="signup-link" style={{ fontWeight: '600' }}>
                              Sign up here →
                            </Link>
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* reCAPTCHA */}
                  <div style={{ marginBottom: '1.25rem' }}>
                    <ReCaptcha
                      siteKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                      onVerify={setRecaptchaToken}
                      theme="light"
                      width='100%'
                    />
                  </div>

                  <button type="submit" disabled={verifying} className="login-primary-btn">
                    <div className="shimmer-overlay" style={{ animation: verifying ? 'none' : 'shimmer 3s infinite' }} />
                    {verifying ? (
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                        <i className="bi bi-arrow-clockwise" style={{ animation: 'spin 0.8s linear infinite', fontSize: '1rem' }}></i>
                        Verifying...
                      </span>
                    ) : (
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                        Continue
                        <i className="bi bi-arrow-right" style={{ fontSize: '1rem', transition: 'transform 0.2s ease' }}></i>
                      </span>
                    )}
                  </button>
                </form>

                {/* Sign Up Link */}
                <div className="text-center" style={{ marginTop: '1.25rem', marginBottom: '1rem' }}>
                  <p style={{ color: '#94a3b8', fontSize: '0.83rem', margin: 0, fontFamily: 'Inter, sans-serif' }}>
                    Don't have an account?{' '}
                    <Link to="/signup" className="signup-link">{t('common.signup')}</Link>
                  </p>
                </div>

                {/* Divider */}
                <div style={{ display: 'flex', alignItems: 'center', margin: '0.75rem 0 1rem' }}>
                  <div className="login-divider-line" />
                  <span className="login-divider-text">or</span>
                  <div className="login-divider-line" />
                </div>

                {/* Google Login */}
                <div className="text-center">
                  <div className="google-login-wrapper">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      useOneTap
                      theme="outline"
                      size="medium"
                      text="signin_with"
                      shape="rectangular"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ========== STEP 2: PASSWORD ========== */}
            {loginStep === 'password' && (
              <div
                key="step-password"
                className={
                  slideDirection === 'left' ? 'step-slide-right' : 'step-slide-left'
                }
              >
                {/* Email identity chip */}
                <div className="email-chip">
                  <div className="email-avatar">{emailInitial}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="email-chip-label">Signing in as</div>
                    <div className="email-chip-value">{email}</div>
                  </div>
                  <button type="button" onClick={handleBackToEmail} className="change-email-btn">
                    Edit
                  </button>
                </div>

                <form onSubmit={handleSubmit} noValidate>
                  <div style={{ marginBottom: '1rem' }}>
                    <label htmlFor="admin-password" className="login-label">Password</label>
                    <div style={{ position: 'relative' }}>
                      <div className="input-icon-wrapper">
                        <i className="bi bi-lock"></i>
                      </div>
                      <input
                        id="admin-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder={t('auth.passwordPlaceholder')}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={loading}
                        autoFocus
                        className="login-input password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="password-toggle-btn"
                      >
                        <i className={`bi ${showPassword ? 'bi-eye' : 'bi-eye-slash'}`}></i>
                      </button>
                    </div>
                  </div>

                  {/* Forgot Password Link */}
                  <div className="text-end" style={{ marginBottom: '1rem' }}>
                    <Link to="/reset-password" className="forgot-password-link">
                      {t('auth.forgotPassword')}
                    </Link>
                  </div>

                  <button type="submit" disabled={loading} className="login-primary-btn">
                    <div className="shimmer-overlay" style={{ animation: loading ? 'none' : 'shimmer 3s infinite' }} />
                    {loading ? (
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                        <i className="bi bi-arrow-clockwise" style={{ animation: 'spin 0.8s linear infinite', fontSize: '1rem' }}></i>
                        {t('common.loading')}
                      </span>
                    ) : (
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                        <i className="bi bi-box-arrow-in-right" style={{ fontSize: '1rem' }}></i>
                        {t('common.login')}
                      </span>
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* Footer */}
            <div className="text-center" style={{ marginTop: '1.5rem' }}>
              <p style={{ color: '#cbd5e1', fontSize: '0.7rem', margin: 0, fontFamily: 'Inter, sans-serif', letterSpacing: '0.02em' }}>
                © {currentYear} AptiView. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Animations & Hover States */}
      <style jsx>{`
        .login-container {
          background: linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%);
          display: flex;
          padding: 0;
          position: relative;
          overflow: hidden;
        }

        .login-bg-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.08) 0%, transparent 50%);
          pointer-events: none;
          animation: backgroundPulse 10s ease-in-out infinite alternate;
        }

        .orb {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.18) 0%, transparent 70%);
        }

        .orb-left {
          top: 15%;
          left: 5%;
          width: 200px;
          height: 200px;
          filter: blur(60px);
          animation: floatOrb 15s ease-in-out infinite;
        }

        .orb-right {
          bottom: 10%;
          right: 10%;
          width: 250px;
          height: 250px;
          filter: blur(70px);
          animation: floatOrb 18s ease-in-out infinite 3s;
        }

        .login-column-wrapper {
          min-height: 100vh;
          overflow-y: auto;
          align-items: flex-start;
        }

        .login-card-wrapper {
          max-width: 420px;
          animation: slideInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
          background: rgba(255, 255, 255, 0.97);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border-radius: 1.75rem;
          box-shadow: 
            0 32px 64px -16px rgba(0, 0, 0, 0.25),
            0 0 0 1px rgba(255, 255, 255, 0.4),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.35);
          padding: 2.25rem 2rem 1.75rem;
          overflow: hidden;
          position: relative;
          margin-top: 10vh !important;
        }

        .login-accent-bar {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #0ea5e9, #8b5cf6, #d946ef, #0ea5e9);
          background-size: 200% 100%;
          animation: gradientShift 4s linear infinite;
          border-radius: 1.75rem 1.75rem 0 0;
        }

        .logo-img {
          transform: scale(0.75);
          max-width: 100%;
          height: auto;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.06));
        }

        .login-welcome-title {
          font-family: 'Poppins', sans-serif;
          font-weight: 800;
          font-size: 1.6rem;
          background: linear-gradient(135deg, #0ea5e9 0%, #8b5cf6 50%, #d946ef 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 0.35rem;
          letter-spacing: -0.02em;
        }

        .login-welcome-subtitle {
          color: #94a3b8;
          font-size: 0.85rem;
          font-weight: 500;
          margin: 0;
          font-family: 'Inter', sans-serif;
          letter-spacing: 0.01em;
        }

        .step-indicator {
          display: flex;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 1rem;
        }

        .indicator-dot {
          height: 8px;
          border-radius: 100px;
          background: #e2e8f0;
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .indicator-dot.active-email {
          width: 24px;
          background: linear-gradient(135deg, #0ea5e9, #8b5cf6);
        }

        .indicator-dot.active-password {
          width: 24px;
          background: linear-gradient(135deg, #8b5cf6, #d946ef);
        }

        .indicator-dot.inactive {
          width: 8px;
        }

        .login-label {
          display: block;
          margin-bottom: 0.5rem;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          color: #334155;
          font-size: 0.8rem;
          letter-spacing: 0.02em;
        }

        .input-icon-wrapper {
          position: absolute;
          left: 0.875rem;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          font-size: 1rem;
          transition: color 0.2s ease;
          pointer-events: none;
          z-index: 1;
        }

        .input-icon-wrapper.error {
          color: #ef4444;
        }

        .login-input {
          width: 100%;
          padding: 0.8rem 0.875rem 0.8rem 2.75rem;
          font-size: 0.9rem;
          color: #1e293b;
          background: #f8fafc;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
          font-family: 'Inter', sans-serif;
          outline: none;
          letter-spacing: 0.01em;
        }

        .login-input.password {
          padding-right: 2.75rem;
          letter-spacing: 0.04em;
        }

        .login-input.error {
          background: rgba(254, 242, 242, 0.5) !important;
          border-color: #fca5a5 !important;
        }

        .error-msg-box {
          margin-top: 0.6rem;
          padding: 0.65rem 0.85rem;
          background: linear-gradient(135deg, rgba(254, 226, 226, 0.5), rgba(254, 215, 215, 0.3));
          border: 1px solid rgba(252, 165, 165, 0.3);
          border-radius: 10px;
          font-size: 0.8rem;
          color: #b91c1c;
          line-height: 1.5;
          animation: shakeAndFade 0.4s ease-out;
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
        }

        .login-primary-btn {
          width: 100%;
          padding: 0.85rem 1.5rem;
          font-size: 0.9rem;
          font-weight: 600;
          font-family: 'Inter', sans-serif;
          border-radius: 12px;
          border: none;
          background: linear-gradient(135deg, #0ea5e9 0%, #8b5cf6 50%, #d946ef 100%);
          background-size: 200% 100%;
          color: white;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 4px 16px -2px rgba(139, 92, 246, 0.35);
          position: relative;
          overflow: hidden;
          letter-spacing: 0.02em;
        }

        .shimmer-overlay {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
          pointer-events: none;
        }

        .login-divider-line {
          flex: 1;
          height: 1px;
          background: linear-gradient(90deg, transparent, #e2e8f0 50%, transparent);
        }

        .login-divider-text {
          padding: 0 1rem;
          color: #94a3b8;
          font-weight: 500;
          font-size: 0.75rem;
          font-family: 'Inter', sans-serif;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        .google-login-wrapper {
          display: inline-block;
          padding: 0.25rem;
          border-radius: 12px;
          background: #fff;
          border: 1.5px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          transition: all 0.2s ease;
        }

        .email-chip {
          display: flex;
          align-items: center;
          gap: 0.65rem;
          padding: 0.55rem 0.65rem 0.55rem 0.55rem;
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.04), rgba(14, 165, 233, 0.04));
          border: 1.5px solid rgba(139, 92, 246, 0.12);
          border-radius: 14px;
          margin-bottom: 1.25rem;
          transition: all 0.2s ease;
        }

        .email-avatar {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: linear-gradient(135deg, #8b5cf6, #0ea5e9);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 0.85rem;
          font-family: 'Inter', sans-serif;
          flex-shrink: 0;
          box-shadow: 0 2px 8px rgba(139, 92, 246, 0.3);
        }

        .email-chip-label {
          font-size: 0.7rem;
          color: #94a3b8;
          font-weight: 500;
          font-family: 'Inter', sans-serif;
          line-height: 1;
          margin-bottom: 0.15rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .email-chip-value {
          font-size: 0.85rem;
          color: #1e293b;
          font-weight: 600;
          font-family: 'Inter', sans-serif;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          line-height: 1.2;
        }

        .change-email-btn {
          background: rgba(139, 92, 246, 0.06);
          border: 1px solid rgba(139, 92, 246, 0.12);
          color: #7c3aed;
          cursor: pointer;
          font-size: 0.75rem;
          font-weight: 600;
          font-family: 'Inter', sans-serif;
          padding: 0.3rem 0.6rem;
          border-radius: 8px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.3rem;
          flex-shrink: 0;
        }

        .password-toggle-btn {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          font-size: 1rem;
          padding: 0.25rem;
          transition: color 0.2s ease;
          z-index: 1;
        }

        .forgot-password-link {
          color: #7c3aed;
          text-decoration: none;
          font-weight: 500;
          font-size: 0.78rem;
          transition: all 0.2s ease;
          font-family: 'Inter', sans-serif;
        }

        .step-active {
          animation: fadeInUp 0.5s ease-out;
        }

        .step-slide-left {
          animation: stepSlideInLeft 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .step-slide-right {
          animation: stepSlideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes stepSlideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes stepSlideInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes shakeAndFade {
          0% {
            opacity: 0;
            transform: translateX(-8px);
          }
          25% {
            transform: translateX(5px);
          }
          50% {
            transform: translateX(-3px);
          }
          75% {
            opacity: 1;
            transform: translateX(1px);
          }
          100% {
            transform: translateX(0);
          }
        }

        @keyframes shimmer {
          0% { left: -100%; }
          50% { left: 100%; }
          100% { left: 100%; }
        }

        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }

        @keyframes floatOrb {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -30px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }

        @keyframes backgroundPulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
        }

        .login-primary-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px -4px rgba(139, 92, 246, 0.45) !important;
          background-position: 100% 0 !important;
        }

        .login-primary-btn:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: 0 2px 8px -2px rgba(139, 92, 246, 0.3) !important;
        }

        .login-input:focus {
          border-color: #8b5cf6 !important;
          box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.08) !important;
          background: #fff !important;
        }

        .email-chip:hover {
          border-color: rgba(139, 92, 246, 0.2) !important;
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.06), rgba(14, 165, 233, 0.06)) !important;
        }

        .change-email-btn:hover {
          background: rgba(139, 92, 246, 0.12) !important;
          border-color: rgba(139, 92, 246, 0.2) !important;
        }

        .signup-link:hover {
          color: #6d28d9 !important;
          border-bottom-color: #7c3aed !important;
        }

        .login-card-wrapper:hover {
          box-shadow:
            0 32px 64px -16px rgba(0, 0, 0, 0.2),
            0 0 0 1px rgba(255, 255, 255, 0.4),
            inset 0 1px 0 rgba(255, 255, 255, 0.8),
            0 0 80px -20px rgba(139, 92, 246, 0.12) !important;
        }

        @media (max-width: 991.98px) {
          .col-12 {
            padding: 1rem !important;
          }
        }

        @media (max-width: 575.98px) {
          .col-12 {
            padding: 0.75rem !important;
          }
        }

        body {
          overflow-x: hidden;
        }

        ::-webkit-scrollbar {
          width: 6px;
        }

        ::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
        }

        ::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }
      `}
      </style>
    </div>
  );
};
